import { apiClient, handleApiError } from './api'

export const businessService = {
  // Get business profile
  async getBusinessProfile() {
    try {
      const response = await apiClient.get('/api/business/profile')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Update business profile
  async updateBusinessProfile(profileData) {
    try {
      const response = await apiClient.patch('/api/business/profile', profileData)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Update business information (for onboarding)
  async updateBusinessInfo(businessData) {
    try {
      const response = await apiClient.post('/api/business/info', businessData)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get dashboard data
  async getDashboardData(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      if (params.location_id) queryParams.append('location_id', params.location_id)
      if (params.period) queryParams.append('period', params.period)
      if (params.start_date) queryParams.append('start_date', params.start_date)
      if (params.end_date) queryParams.append('end_date', params.end_date)
      
      const url = `/api/business/dashboard${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      const response = await apiClient.get(url)
      
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get business locations
  async getBusinessLocations() {
    try {
      const response = await apiClient.get('/api/business/locations')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get single location
  async getLocation(locationId) {
    try {
      const response = await apiClient.get(`/api/business/locations/${locationId}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Update location settings
  async updateLocation(locationId, updates) {
    try {
      const response = await apiClient.patch(`/api/business/locations/${locationId}`, updates)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Google Business Profile Integration
  async getGoogleAuthStatus() {
    try {
      const response = await apiClient.get('/api/google/oauth/status')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get OAuth URL for business account connection
  async getOAuthUrl() {
    try {
      const response = await apiClient.post('/api/google/oauth/initiate')
      return response.data.auth_url
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Initiate Google OAuth
  async initiateGoogleAuth() {
    try {
      const response = await apiClient.post('/api/google/oauth/initiate')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Complete Google OAuth
  async completeGoogleAuth(code, state) {
    try {
      const response = await apiClient.post('/api/google/oauth/callback', {
        code,
        state
      })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Disconnect Google account
  async disconnectGoogle() {
    try {
      const response = await apiClient.post('/api/google/oauth/disconnect')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get Google Business locations
  async getGoogleBusinessLocations() {
    try {
      const response = await apiClient.get('/api/google/oauth/sync')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Search Google Business locations
  async searchGoogleBusinessLocations(query) {
    try {
      const response = await apiClient.get('/api/google/oauth/sync')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Save selected locations
  async saveSelectedLocations(locationIds) {
    try {
      const response = await apiClient.post('/api/business/locations/select', {
        location_ids: locationIds
      })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Sync location data from Google
  async syncLocationData(locationId) {
    try {
      const response = await apiClient.post(`/api/business/locations/${locationId}/sync`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get location analytics
  async getLocationAnalytics(locationId, params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      if (params.period) queryParams.append('period', params.period)
      if (params.start_date) queryParams.append('start_date', params.start_date)
      if (params.end_date) queryParams.append('end_date', params.end_date)
      
      const url = `/api/business/locations/${locationId}/analytics${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      const response = await apiClient.get(url)
      
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Settings Management
  async getSettings() {
    try {
      const response = await apiClient.get('/api/business/settings')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Update settings
  async updateSettings(settings) {
    try {
      const response = await apiClient.patch('/api/business/settings', settings)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get notification settings
  async getNotificationSettings() {
    try {
      const response = await apiClient.get('/api/business/settings/notifications')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Update notification settings
  async updateNotificationSettings(settings) {
    try {
      const response = await apiClient.patch('/api/business/settings/notifications', settings)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Reply Templates
  async getReplyTemplates() {
    try {
      const response = await apiClient.get('/api/business/reply-templates')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Create reply template
  async createReplyTemplate(template) {
    try {
      const response = await apiClient.post('/api/business/reply-templates', template)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Update reply template
  async updateReplyTemplate(templateId, updates) {
    try {
      const response = await apiClient.patch(`/api/business/reply-templates/${templateId}`, updates)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Delete reply template
  async deleteReplyTemplate(templateId) {
    try {
      const response = await apiClient.delete(`/api/business/reply-templates/${templateId}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Team Management
  async getTeamMembers() {
    try {
      const response = await apiClient.get('/api/business/team')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Invite team member
  async inviteTeamMember(invitation) {
    try {
      const response = await apiClient.post('/api/business/team/invite', invitation)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Update team member role
  async updateTeamMemberRole(memberId, role) {
    try {
      const response = await apiClient.patch(`/api/business/team/${memberId}/role`, { role })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Remove team member
  async removeTeamMember(memberId) {
    try {
      const response = await apiClient.delete(`/api/business/team/${memberId}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Integrations
  async getIntegrations() {
    try {
      const response = await apiClient.get('/api/business/integrations')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Update integration settings
  async updateIntegration(integrationType, settings) {
    try {
      const response = await apiClient.patch(`/api/business/integrations/${integrationType}`, settings)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Test integration
  async testIntegration(integrationType) {
    try {
      const response = await apiClient.post(`/api/business/integrations/${integrationType}/test`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Billing and Subscription
  async getSubscription() {
    try {
      const response = await apiClient.get('/api/business/subscription')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Update subscription
  async updateSubscription(planId) {
    try {
      const response = await apiClient.post('/api/business/subscription/update', { plan_id: planId })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get billing history
  async getBillingHistory() {
    try {
      const response = await apiClient.get('/api/business/billing/history')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Reports and Analytics
  async generateReport(reportType, params = {}) {
    try {
      const response = await apiClient.post('/api/business/reports/generate', {
        type: reportType,
        ...params
      })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get report history
  async getReportHistory() {
    try {
      const response = await apiClient.get('/api/business/reports/history')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Download report
  async downloadReport(reportId) {
    try {
      const response = await apiClient.get(`/api/business/reports/${reportId}/download`, {
        responseType: 'blob'
      })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Onboarding
  async completeOnboarding() {
    try {
      const response = await apiClient.post('/api/business/onboarding/complete')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get onboarding status
  async getOnboardingStatus() {
    try {
      const response = await apiClient.get('/api/business/onboarding/status')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Business Hours
  async getBusinessHours(locationId) {
    try {
      const response = await apiClient.get(`/api/business/locations/${locationId}/hours`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Update business hours
  async updateBusinessHours(locationId, hours) {
    try {
      const response = await apiClient.patch(`/api/business/locations/${locationId}/hours`, { hours })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Competitor Analysis
  async getCompetitors() {
    try {
      const response = await apiClient.get('/api/business/competitors')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Add competitor
  async addCompetitor(competitorData) {
    try {
      const response = await apiClient.post('/api/business/competitors', competitorData)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Remove competitor
  async removeCompetitor(competitorId) {
    try {
      const response = await apiClient.delete(`/api/business/competitors/${competitorId}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get competitor analysis
  async getCompetitorAnalysis(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      if (params.period) queryParams.append('period', params.period)
      if (params.location_id) queryParams.append('location_id', params.location_id)
      
      const url = `/api/business/competitors/analysis${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      const response = await apiClient.get(url)
      
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Backup and Export
  async exportBusinessData(format = 'json') {
    try {
      const response = await apiClient.get(`/api/business/export?format=${format}`, {
        responseType: 'blob'
      })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Import business data
  async importBusinessData(file) {
    try {
      const formData = new FormData()
      formData.append('file', file)
      
      const response = await apiClient.post('/api/business/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Health Check
  async getSystemHealth() {
    try {
      const response = await apiClient.get('/api/business/health')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Activity Log
  async getActivityLog(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      if (params.page) queryParams.append('page', params.page)
      if (params.limit) queryParams.append('limit', params.limit)
      if (params.action_type) queryParams.append('action_type', params.action_type)
      if (params.start_date) queryParams.append('start_date', params.start_date)
      if (params.end_date) queryParams.append('end_date', params.end_date)
      
      const url = `/api/business/activity${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      const response = await apiClient.get(url)
      
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Business Location Management
  async searchBusinessByName(businessName, location) {
    try {
      const response = await apiClient.get('/api/business/search', {
        params: { name: businessName, location }
      })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Add business location
  async addBusinessLocation(businessData) {
    try {
      const response = await apiClient.post('/api/business/locations', businessData)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get place ID for business
  async getPlaceId(businessName, location) {
    try {
      const response = await apiClient.get('/api/business/place-id', {
        params: { business_name: businessName, location }
      })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Scrape reviews for a business
  async scrapeReviews(placeId, maxReviews = 150) {
    try {
      const response = await apiClient.post(`/api/business/${placeId}/scrape-reviews?max_reviews=${maxReviews}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get scraping status by task ID
  async getScrapingStatus(taskId) {
    try {
      const response = await apiClient.get(`/api/business/scraping-status/${taskId}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Get business statistics
  async getBusinessStats(placeId) {
    try {
      const response = await apiClient.get(`/api/business/stats/${placeId}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // AI Reply Processing
  async processAIReplies(placeId, forceProcess = false) {
    try {
      const response = await apiClient.post(`/api/business/${placeId}/process-ai-replies?force_process=${forceProcess}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Goals Management
  async getBusinessGoals(placeId) {
    try {
      const response = await apiClient.get(`/api/business/${placeId}/goals`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async setBusinessGoals(placeId, goals) {
    try {
      const response = await apiClient.post(`/api/business/${placeId}/goals`, goals)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async getAllBusinessGoals() {
    try {
      const response = await apiClient.get('/api/business/goals')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async deleteBusinessGoals(placeId) {
    try {
      const response = await apiClient.delete(`/api/business/${placeId}/goals`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  }
}

export default businessService